/**
 * ConfirmDialog — Confirmation modal for destructive actions (delete).
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faTimes, faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiPaper-root': {
      minWidth: 300,
    },
  },
  title: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  content: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  description: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  actions: {
    paddingTop: theme.spacing(1),
  },
  dangerButton: {
    marginRight: 'auto',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  dangerButtonText: {
    textTransform: 'none',
    color: theme.palette.error.contrastText,
    fontWeight: 500,
  },
  confirmButtonText: {
    textTransform: 'none',
  },
  cancelButtonText: {
    textTransform: 'none',
  },
  actionIcon: {
    fontSize: '16px',
    marginRight: theme.spacing(1),
  },
}));

export interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  isDangerous?: boolean;
}

/**
 * Confirmation dialog for destructive actions.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmText,
  cancelText,
  loading,
  onConfirm,
  onCancel,
  isDangerous = true,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('accounting');

  const displayTitle = title || t('confirmDialog:deleteTitle', { defaultValue: 'Confirm Delete' });
  const displayMessage = message || t('confirmDialog:deleteMessage', { defaultValue: 'This action cannot be undone.' });
  const displayConfirm = confirmText || t('common:delete', { defaultValue: 'Delete' });
  const displayCancel = cancelText || t('common:cancel', { defaultValue: 'Cancel' });

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      className={classes.dialog}
    >
      <DialogTitle className={classes.title}>
        {displayTitle}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Typography className={classes.description}>
          {displayMessage}
        </Typography>
      </DialogContent>
      <DialogActions className={classes.actions}>
        {isDangerous ? (
          <>
            <Button
              onClick={onConfirm}
              disabled={loading}
              className={classes.dangerButton}
            >
              {loading ? (
                <FontAwesomeIcon spin icon={faSpinner} className={classes.actionIcon} />
              ) : (
                <FontAwesomeIcon icon={faTrashAlt} className={classes.actionIcon} />
              )}
              <Typography variant="body2" className={classes.dangerButtonText}>
                {displayConfirm}
              </Typography>
            </Button>
            <Button
              onClick={onCancel}
              disabled={loading}
              variant="outlined"
            >
              <FontAwesomeIcon icon={faTimes} className={classes.actionIcon} />
              <Typography variant="body2" className={classes.cancelButtonText}>
                {displayCancel}
              </Typography>
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={onCancel}
              disabled={loading}
              variant="outlined"
            >
              <FontAwesomeIcon icon={faTimes} className={classes.actionIcon} />
              <Typography variant="body2" className={classes.cancelButtonText}>
                {displayCancel}
              </Typography>
            </Button>
            <Button
              onClick={onConfirm}
              color="primary"
              variant="contained"
              disabled={loading}
            >
              {loading ? (
                <FontAwesomeIcon spin icon={faSpinner} className={classes.actionIcon} />
              ) : (
                <FontAwesomeIcon icon={faCheck} className={classes.actionIcon} />
              )}
              <Typography variant="body2" className={classes.confirmButtonText}>
                {displayConfirm}
              </Typography>
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
